import { Link } from "react-router-dom";
import { CalendarDays, MapPin, FileText, User, Search as SearchIcon, ChevronDown, Coffee, Bike, Palette, BookOpen, Trees, Map as MapIcon } from "lucide-react";

import { cn } from "@/lib/utils";
import { useI18n } from "@/i18n/I18nContext";
import { text, participationState } from "@/lib/memberDerive";
import { activityWhen, activityPlace, spotsText } from "@/lib/v3Views";
import { useMemberAuth } from "../MemberAuthContext";
import { shortName } from "../navSections";
import { Avatar } from "./kit";
import { AsideCard, V3_PRIMARY } from "./v3";

// V3 app building blocks (Start, Community, Suche); sizes follow the 1448 px / 390 px frames.
export const V3_CARD = "rounded-[12px] border border-v3-line bg-white";
export const V3_LABEL = "mb-[12px] block text-[14px] font-bold leading-[18px] text-v3-ink lg:text-[15px] lg:leading-[20px]";
export const V3_LINK = "inline-flex min-h-[44px] items-center font-bold text-v3-magenta underline-offset-4 hover:underline";
export const V3_SMALL = "inline-flex min-h-[44px] items-center gap-2 rounded-[8px] border border-v3-line bg-white px-[14px] text-[14px] font-bold text-v3-ink transition-colors hover:bg-v3-lilac disabled:opacity-60";
export const V3_GHOST = "inline-flex min-h-[44px] items-center gap-2 rounded-[8px] px-[14px] text-[14px] font-bold text-v3-muted transition-colors hover:bg-v3-lilac hover:text-v3-ink disabled:opacity-60";

export function ResponsibleCard() {
  const { member } = useMemberAuth();
  const n = shortName(member?.name);
  return <AsideCard icon={User} tone="white" title="Verantwortung" text={n ? `${n} · Mitglied` : "Mitglied"} testid="v3-responsible" />;
}

export const AreaCard = ({ title = "Dein Bereich", text: body, icon = FileText, testid = "v3-area" }) => <AsideCard icon={icon} title={title} text={body} testid={testid} />;

const PILL = { lilac: "bg-v3-lilac text-v3-muted", mint: "bg-v3-mint text-v3-green", sand: "bg-v3-sand text-v3-amber", red: "bg-red-50 text-red-800", ink: "bg-v3-lilac text-v3-ink" };
export const Pill = ({ tone = "lilac", children, testid }) => (
  <span data-testid={testid} className={`inline-flex min-h-[29px] items-center rounded-full px-[12px] text-[12.5px] font-bold ${PILL[tone]}`}>{children}</span>
);

const PARTICIPATION = { confirmed: ["mint", "Deine Teilnahme ist bestätigt"], registered: ["mint", "Teilnahme eingetragen"], requested: ["sand", "Anfrage ausstehend"], waitlisted: ["sand", "Auf der Warteliste"], offered: ["sand", "Platz angeboten"],
  cancelled: ["lilac", "Von dir abgesagt"], activity_cancelled: ["red", "Aktivität abgesagt"] };
export function ParticipationPill({ a, testid }) {
  const p = PARTICIPATION[participationState(a)];
  return p ? <Pill tone={p[0]} testid={testid}>{p[1]}</Pill> : null;
}

const CATEGORY_ICON = { soziales: Coffee, sport: Bike, kultur: Palette, bildung: BookOpen, freizeit: Trees, regional: MapIcon };

// No activity photos in the Core (V.9): a neutral category area keeps the V3 image slot without inventing a picture.
export function ActivityVisual({ a, className = "" }) {
  const { t } = useI18n();
  const key = typeof a.category_key === "string" && CATEGORY_ICON[a.category_key] ? a.category_key : "";
  const Icon = CATEGORY_ICON[key] || CalendarDays;
  return (
    <div aria-hidden="true" data-testid="activity-visual" className={`flex flex-col items-center justify-center gap-[10px] rounded-[10px] bg-v3-lilac text-v3-magenta ${className}`}>
      <Icon className="h-[34px] w-[34px]" strokeWidth={1.6} />
      <span className="text-[12.5px] font-bold text-v3-muted">{key ? t(`member.categories.${key}`) : "Aktivität"}</span>
    </div>
  );
}

export function ActivityFacts({ a, hero }) {
  const icon = `h-[16px] w-[16px] shrink-0 text-v3-muted ${hero ? "lg:hidden" : ""}`;
  return (
    <div className={`mt-[14px] space-y-[12px] ${hero ? "lg:mt-[18px] lg:space-y-[11px]" : ""}`}>
      <p className={`flex items-center gap-[12px] text-[13.5px] text-v3-ink ${hero ? "lg:text-[15px]" : ""}`}><CalendarDays className={icon} aria-hidden="true" />{activityWhen(a)}</p>
      <p className="flex items-center gap-[12px] text-[13.5px] text-v3-muted lg:text-[14px]"><MapPin className={icon} aria-hidden="true" /><span className="min-w-0 break-words">{activityPlace(a)}</span></p>
    </div>
  );
}

const detailHref = (a) => `/aktivitaeten/${encodeURIComponent(a.id)}`;

export function NextActivityCard({ a }) {
  const title = text(a.title) || "Aktivität";
  const desc = text(a.description);
  return (
    <article data-testid="start-next-activity" className={`${V3_CARD} grid gap-[15px] p-[10px] pb-[16px] lg:grid-cols-[321px_minmax(0,1fr)] lg:gap-[20px]`}>
      <ActivityVisual a={a} className="h-[150px] lg:h-[280px]" />
      <div className="min-w-0 px-[7px] lg:px-0 lg:pr-[8px] lg:pt-[6px]">
        <h2 className="break-words text-[21px] font-bold leading-[28px] text-v3-ink lg:text-[26px] lg:leading-[36px]">{title}</h2>
        <ActivityFacts a={a} hero />
        <div className="mt-[14px] lg:mt-[22px]"><ParticipationPill a={a} testid="start-next-status" /></div>
        {desc && <p className="mt-[12px] hidden text-[14px] leading-[20px] text-v3-muted line-clamp-2 lg:block">{desc}</p>}
        <Link to={detailHref(a)} data-testid="start-next-open" className={cn(V3_PRIMARY, "mt-[14px] lg:mt-[21px] lg:w-full")}>Termin ansehen<span className="sr-only">: {title}</span></Link>
      </div>
    </article>
  );
}

export function NearbyCard({ a }) {
  const title = text(a.title) || "Aktivität";
  const spots = spotsText(a);
  return (
    <article data-testid={`member-activity-${a.id}`} className={`${V3_CARD} flex flex-col p-[10px] pb-[9px]`}>
      <ActivityVisual a={a} className="h-[150px] lg:h-[154px]" />
      <div className="flex flex-1 flex-col px-[7px] pt-[13px]">
        <h3 className="break-words text-[21px] font-bold leading-[28px] text-v3-ink lg:text-[20px]">{title}</h3>
        <ActivityFacts a={a} />
        <div className="mt-[14px] flex flex-wrap gap-2">
          {spots && <Pill testid={`activity-spots-${a.id}`}>{spots}</Pill>}
          <ParticipationPill a={a} testid={`activity-status-${a.id}`} />
        </div>
        <div className="mt-auto pt-[14px]"><Link to={detailHref(a)} data-testid={`activity-open-${a.id}`} className={cn(V3_PRIMARY, "lg:w-full")}>Details ansehen<span className="sr-only">: {title}</span></Link></div>
      </div>
    </article>
  );
}

export function ActionTile({ to, icon: Icon, title, text: body, testid }) {
  return (
    <Link to={to} data-testid={testid} className={`${V3_CARD} block px-[24px] pb-[21px] pt-[24px] transition-colors hover:border-v3-magenta focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-v3-magenta`}>
      <span className="flex items-center gap-[26px]"><Icon className="h-[30px] w-[30px] shrink-0 text-v3-magenta" aria-hidden="true" />
        <span className="text-[25px] font-bold leading-[34px] text-v3-ink lg:text-[30px] lg:leading-[40px]">{title}</span></span>
      <span className="mt-[24px] block text-[17px] leading-[24px] text-v3-muted lg:mt-[18px]">{body}</span>
    </Link>
  );
}

export function PersonCard({ name, context, color, testid }) {
  return (
    <div data-testid={testid} className={`${V3_CARD} flex min-h-[82px] items-center gap-[15px] px-[16px] py-[12px]`}>
      <Avatar name={name} color={color} size="h-[44px] w-[44px] text-[13px]" />
      <div className="min-w-0">
        <p className="break-words text-[17px] font-bold leading-[23px] text-v3-ink">{name}</p>
        {context && <p className="mt-[6px] break-words text-[12.5px] leading-[17px] text-v3-muted">{context}</p>}
      </div>
    </div>
  );
}

export function SearchField({ id, value, onChange, placeholder, label, testid, disabled, describedBy }) {
  return (
    <div className="relative">
      <label htmlFor={id} className="sr-only">{label}</label>
      <SearchIcon className="pointer-events-none absolute left-[15px] top-1/2 h-[18px] w-[18px] -translate-y-1/2 text-v3-muted" aria-hidden="true" />
      <input id={id} type="search" value={value} onChange={(e) => onChange(e.target.value)} placeholder={placeholder} data-testid={testid} maxLength={100} disabled={disabled} aria-describedby={describedBy}
        className="h-[49px] w-full rounded-[10px] border border-v3-line bg-white pl-[43px] pr-[14px] text-[14px] text-v3-ink outline-none placeholder:text-v3-muted focus:border-v3-magenta disabled:cursor-not-allowed" />
    </div>
  );
}

export function FilterBar({ children, onReset, canReset = true, end, testid }) {
  return (
    <div className="mt-[17px] flex flex-wrap items-center justify-between gap-2" data-testid={testid}>
      <div className="flex flex-wrap items-center gap-2">{children}</div>
      {end}
      {!end && onReset && <button type="button" onClick={onReset} disabled={!canReset} aria-describedby={canReset ? undefined : `${testid}-reset-hint`} data-testid={testid && `${testid}-reset`}
        className="min-h-[44px] rounded-[6px] px-[12px] text-[13px] font-bold text-v3-magenta hover:underline disabled:cursor-not-allowed disabled:opacity-50">Zurücksetzen</button>}
      {!end && onReset && !canReset && <span id={`${testid}-reset-hint`} className="sr-only">Noch kein Filter ausgewählt.</span>}
    </div>
  );
}

export function Chip({ pressed, onClick, children, testid }) {
  return (
    <button type="button" aria-pressed={pressed} onClick={onClick} data-testid={testid}
      className={`inline-flex min-h-[44px] items-center rounded-full px-[14px] text-[12.5px] font-bold transition-colors lg:min-h-[29px] ${pressed ? "bg-v3-ink text-white" : "bg-v3-lilac text-v3-ink hover:bg-v3-line"}`}>{children}</button>
  );
}

// Table look of M42 / M49: header row (desktop) and one card per row.
export function ResultTable({ children, testid, label }) {
  return (
    <div className="mt-[8px]" data-testid={testid}>
      <div aria-hidden="true" className="hidden h-[43px] grid-cols-[minmax(0,433fr)_minmax(0,574fr)_minmax(0,85fr)] items-center rounded-[10px] bg-v3-lilac px-[20px] text-[13px] font-bold text-v3-muted lg:grid">
        <span>Person / Vorgang</span><span>Kontext und Zustand</span><span>Aktion</span>
      </div>
      <ul className="mt-[11px] space-y-[11px]" aria-label={label}>{children}</ul>
    </div>
  );
}

const ROW = "grid min-h-[91px] w-full grid-cols-1 items-center rounded-[10px] border border-v3-line bg-white px-[16px] py-[20px] text-left lg:grid-cols-[minmax(0,437fr)_minmax(0,574fr)_minmax(0,89fr)]";
const ROW_ACTIVE = `${ROW} transition-colors hover:border-v3-magenta focus-visible:outline focus-visible:outline-2 focus-visible:outline-v3-magenta`;

export function ResultRow({ initial, title, context, to, onOpen, expanded, action = "Öffnen", chevron = Boolean(onOpen), testid, children }) {
  const body = (<>
    <span className="flex min-w-0 items-center gap-[17px] lg:pr-[16px]">
      <span aria-hidden="true" className="flex h-[42px] w-[42px] shrink-0 items-center justify-center rounded-full bg-v3-lilac text-[12.6px] font-bold uppercase text-v3-ink">{initial}</span>
      <span className="min-w-0"><span className="block break-words text-[16px] font-bold leading-[22px] text-v3-ink">{title}</span>
        <span className="mt-[8px] block break-words text-[14px] leading-[19px] text-v3-muted lg:hidden">{context}</span></span>
    </span>
    <span className="hidden break-words pr-4 text-[14px] text-v3-muted lg:block">{context}</span>
    <span className="hidden items-center gap-1 text-[14px] font-bold text-v3-magenta lg:flex">{action}{chevron && <ChevronDown className={`h-4 w-4 transition-transform ${expanded ? "rotate-180" : ""}`} aria-hidden="true" />}</span>
  </>);
  return (
    <li data-testid={testid}>
      {to ? <Link to={to} className={ROW_ACTIVE}>{body}</Link>
        : onOpen ? <button type="button" onClick={onOpen} aria-expanded={expanded} data-testid={testid && `${testid}-toggle`} className={ROW_ACTIVE}>{body}</button>
        : <div className={ROW}>{body}</div>}
      {children}
    </li>
  );
}

export function V3Select({ id, label, value, onChange, children, testid, hint, error, disabled }) {
  const describedBy = [hint && `${id}-hint`, error && `${id}-error`].filter(Boolean).join(" ") || undefined;
  return (
    <div className="mb-[18px]">
      <label htmlFor={id} className={V3_LABEL}>{label}</label>
      <div className="relative">
        <select id={id} value={value} onChange={onChange} disabled={disabled} data-testid={testid} aria-describedby={describedBy} aria-invalid={error ? "true" : undefined}
          className={`h-[52px] w-full cursor-pointer appearance-none rounded-[8px] border bg-white pl-[14px] pr-[40px] text-[14px] text-v3-ink outline-none focus:border-v3-magenta disabled:cursor-not-allowed disabled:opacity-60 lg:text-[15px] ${error ? "border-red-700" : "border-v3-line"}`}>{children}</select>
        <ChevronDown className="pointer-events-none absolute right-[14px] top-1/2 h-4 w-4 -translate-y-1/2 text-v3-muted" aria-hidden="true" />
      </div>
      {hint && <span id={`${id}-hint`} className="mt-[6px] block text-[13px] text-v3-muted">{hint}</span>}
      {error && <span id={`${id}-error`} role="alert" data-testid={`${id}-error`} className="mt-[4px] block text-[13px] font-bold text-red-800">{error}</span>}
    </div>
  );
}

export const SectionTitle = ({ id, children }) => <h2 id={id} className="text-[22px] font-bold leading-[30px] text-v3-ink lg:text-[23px]">{children}</h2>;

// Plain-text area with a code-point counter; longer input is allowed so the error can explain the limit.
export function V3TextArea({ id, label, value, onChange, max, error, hint, testid, rows = 4, placeholder }) {
  const count = Array.from(value || "").length;
  const describedBy = [hint && `${id}-hint`, error && `${id}-error`].filter(Boolean).join(" ") || undefined;
  return (
    <div className="mb-[18px]">
      <div className="mb-[12px] flex items-baseline justify-between gap-2">
        <label htmlFor={id} className="text-[14px] font-bold leading-[18px] text-v3-ink lg:text-[15px] lg:leading-[20px]">{label}</label>
        {max && <span className={`text-[12.5px] ${count > max ? "font-bold text-red-800" : "text-v3-muted"}`} data-testid={`${id}-count`}>{count}/{max}</span>}
      </div>
      <textarea id={id} data-testid={testid || id} value={value} onChange={onChange} rows={rows} placeholder={placeholder} aria-invalid={error ? "true" : undefined} aria-describedby={describedBy}
        className="min-h-[112px] w-full resize-y rounded-[8px] border border-v3-line bg-white px-[14px] py-[14px] text-[14px] leading-[21px] text-v3-ink outline-none placeholder:text-v3-muted focus:border-v3-magenta aria-[invalid=true]:border-red-700 lg:text-[15px]" />
      {hint && <span id={`${id}-hint`} className="mt-[6px] block text-[13px] text-v3-muted">{hint}</span>}
      {error && <span id={`${id}-error`} role="alert" data-testid={`${id}-error`} className="mt-[6px] block text-[13px] font-bold text-red-800">{error}</span>}
    </div>
  );
}
